import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, makeStyles, IconButton, Button } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import PopupModal from 'views/UserMessaging/customModalContent/popupModal';
import { getEnvVar } from 'helpers';

const useStyles = makeStyles(theme => ({
  uploadsPanelContainer: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'rgb(249, 249, 249)',
    overflow: 'hidden'
  },
  galleryGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignContent: 'flex-start',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(0.5, 1, 1, 0.5),
    boxSizing: 'border-box'
  },
  builderGalleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(0.5, 1, 1, 0.5),
    boxSizing: 'border-box'
  },
  galleryItem: {
    width: 'calc((100% - 16px) / 2)',
    minWidth: 0
  },
  thumbWrapper: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #ddd',
    borderRadius: 4,
    backgroundColor: '#fff',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
      borderColor: '#9aa4b2'
    }
  },
  thumbImage: {
    width: '100%',
    height: 90,
    objectFit: 'cover',
    borderBottom: '1px solid #eee',
    display: 'block',
    cursor: 'pointer'
  },
  thumbMeta: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  thumbHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5)
  },
  thumbName: {
    fontSize: 12,
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  copyIconButton: {
    padding: 4
  },
  copyIcon: {
    fontSize: 15,
    color: '#666'
  },
  thumbSize: {
    fontSize: 11,
    color: '#666'
  },
  thumbType: {
    fontSize: 10,
    color: '#999',
    fontStyle: 'italic'
  },
  uploadsPanelContent: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    textAlign: 'center',
    color: '#666',
    padding: theme.spacing(3)
  },
  copyStatus: {
    marginTop: theme.spacing(1),
    fontSize: 12,
    color: '#1f6f43'
  },
  uploadsPanelHeader: {
    marginTop: -theme.spacing(3),
    marginRight: -theme.spacing(3),
    marginLeft: -theme.spacing(3),
    marginBottom: 0,
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: '#fff',
    borderBottom: '1px solid #d4d4d4'
  },
  uploadsButtonHeader: {
    marginRight: -theme.spacing(3),
    marginBottom: theme.spacing(1.25),
    marginLeft: -theme.spacing(3),
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#fff'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  panelTitle: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '18px',
    color: '#262626'
  },
  topUploadButton: {
    width: '100%',
    minHeight: 40,
    padding: '8px 12px',
    fontSize: 13,
    textTransform: 'none',
    backgroundColor: '#000',
    color: '#fff',
    borderColor: '#000',
    '&:hover': {
      backgroundColor: '#111',
      borderColor: '#111'
    },
    '&.Mui-disabled': {
      backgroundColor: '#444',
      color: '#e5e7eb',
      borderColor: '#444'
    }
  },
  closeButton: {
    padding: 2,
    color: '#515151'
  },
  uploadDropZone: {
    width: '100%',
    minHeight: 120,
    border: '2px dashed #b8bfd1',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: '#f8fafc',
    color: '#4a5568',
    cursor: 'pointer',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    padding: theme.spacing(1.5),
    boxSizing: 'border-box',
    transition: 'all 0.2s ease'
  },
  dropZoneIcon: {
    width: 26,
    height: 26,
    marginBottom: theme.spacing(0.5)
  },
  imageOnlyGalleryItem: {
    width: '100%',
    minWidth: 0
  },
  imageOnlyWrapper: {
    position: 'relative',
    border: '1px solid #ddd',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#fff'
  },
  imageOnlyButton: {
    border: 0,
    width: '100%',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    backgroundColor: '#fff',
    display: 'block'
  },
  imageOnlyImg: {
    width: '100%',
    aspectRatio: '1 / 1',
    objectFit: 'cover',
    display: 'block'
  },
  uploadDropZoneActive: {
    borderColor: '#0b5fff',
    backgroundColor: '#eef4ff',
    color: '#0b3ea8'
  },
  uploadDropZoneHint: {
    fontSize: 11,
    color: '#6b7280'
  },
  deleteIconButton: {
    padding: 4
  },
  deleteIcon: {
    fontSize: 15,
    color: '#b91c1c'
  },
  deleteOverlayButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 3,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: 'rgba(255, 235, 235, 1)'
    }
  },
  deleteOverlayIcon: {
    fontSize: 16,
    color: '#b91c1c'
  }
}));

const UploadsPanel = ({
  isOpen,
  uploads = [],
  onUploadFiles,
  uploadInProgress = false,
  onSelectImage,
  onDeleteImage,
  showHeader = false,
  showTopUploadButton = false,
  onClose,
  panelMode = 'default',
  translations = {}
}) => {
  const classes = useStyles();
  const [copyStatus, setCopyStatus] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAssetToDelete, setSelectedAssetToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const acceptTypes = getEnvVar(`ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS`) || `image/jpeg,image/png,image/jpg`;
  const acceptTypesLabel = getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL') ? getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL') : `JPEG, PNG and JPG`;
  const acceptLimitLabel = getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') ? getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') : `10`;

  const galleryItems = useMemo(() => {
    return (uploads || []).filter(item => {
      const imageUrl = item?.cdnPublicUrl || item?.publicUrl;
      if (!imageUrl) {
        return false;
      }
      if (
        (item?.contentMimeType || item?.contentType || item?.mimeType) &&
        String(item.contentMimeType || item.contentType || item.mimeType)
          .toLowerCase()
          .startsWith('image/')
      ) {
        return true;
      }
      if (item?.type && String(item.type).toLowerCase().startsWith('image/')) {
        return true;
      }
      const lowerUrl = String(imageUrl).toLowerCase();
      return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].some(ext =>
        lowerUrl.includes(ext)
      );
    });
  }, [uploads]);

  const copyToClipboard = async value => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus('Image URL copied to clipboard.');
    } catch (error) {
      setCopyStatus('Unable to copy URL. Please copy manually.');
    }
    setTimeout(() => setCopyStatus(''), 2000);
  };

  const getImageFiles = fileList =>
    Array.from(fileList || []).filter(file =>
      String(file?.type || '').toLowerCase().startsWith('image/')
    );

  const handleFileUpload = files => {
    const imageFiles = getImageFiles(files);
    if (!imageFiles.length || !onUploadFiles) {
      return;
    }
    onUploadFiles(imageFiles);
  };

  const stopDefaults = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleThumbnailClick = (item, imageUrl) => {
    if (onSelectImage) {
      onSelectImage(item, imageUrl);
      return;
    }
    copyToClipboard(imageUrl);
  };

  const handleDeleteClick = asset => {
    setSelectedAssetToDelete(asset);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedAssetToDelete && onDeleteImage) {
      onDeleteImage(selectedAssetToDelete);
    }
    setShowDeleteConfirm(false);
    setSelectedAssetToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSelectedAssetToDelete(null);
  };

  const getDeleteButtonGroup = () => [
    { title: translations?.uploads_panel_delete_btn || 'Delete', action: handleDeleteConfirm },
    { title: translations?.uploads_panel_cancel_btn || 'Cancel', action: handleDeleteCancel }
  ];

  return (
    <Box className={classes.uploadsPanelContainer}>
      <Box className={classes.uploadsPanelContent}>
        {isOpen && showHeader && (
          <Box className={classes.uploadsPanelHeader}>
            <Box className={classes.panelHeader}>
              <Typography className={classes.panelTitle}>
                {translations?.uploads_panel_title || 'Uploads'}
              </Typography>
              {typeof onClose === 'function' && (
                <IconButton
                  className={classes.closeButton}
                  aria-label="Close uploads"
                  onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {isOpen && showTopUploadButton && (
          <Box className={classes.uploadsButtonHeader}>
            <Button
              className={classes.topUploadButton}
              variant="contained"
              size="small"
              disabled={uploadInProgress}
              onClick={() => fileInputRef.current?.click()}>
              {translations?.uploads_panel_upload_button || 'Upload Images'}
            </Button>
          </Box>
        )}

        {isOpen && (
          <Box
            className={`${classes.uploadDropZone} ${isDragActive ? classes.uploadDropZoneActive : ''}`}
            onClick={() => {
              if (!uploadInProgress) {
                fileInputRef.current?.click();
              }
            }}
            onDragEnter={event => {
              stopDefaults(event);
              if (!uploadInProgress) {
                setIsDragActive(true);
              }
            }}
            onDragOver={event => {
              stopDefaults(event);
              if (!uploadInProgress) {
                setIsDragActive(true);
              }
            }}
            onDragLeave={event => {
              stopDefaults(event);
              setIsDragActive(false);
            }}
            onDrop={event => {
              stopDefaults(event);
              setIsDragActive(false);
              if (!uploadInProgress) {
                handleFileUpload(event.dataTransfer?.files);
              }
            }}>
            <CloudUploadOutlinedIcon className={classes.dropZoneIcon} />
            <Typography variant="body2">
              {uploadInProgress
                ? translations?.uploads_panel_uploading || 'Uploading images...'
                : <div>
                    <div>
                      {translations?.uploads_panel_dropzone || 'Drop a new image here, or click to select files to upload.'}
                    </div>
                    <div>
                      {translations?.dataframe_custom_image_note[0] || 'Note: Supports only'} {acceptTypesLabel} {translations?.dataframe_custom_image_note[1] || 'images upto'} {acceptLimitLabel} {translations?.dataframe_custom_image_note[2] || 'MB in size.'}
                    </div>
                  </div>
              }
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes}
              multiple
              style={{ display: 'none' }}
              onChange={event => {
                handleFileUpload(event.target?.files);
                event.target.value = '';
              }}
            />
          </Box>
        )}

        {!isOpen && (
          <Typography variant="body2" className={classes.emptyState}>
            Uploads panel closed
          </Typography>
        )}

        {isOpen && galleryItems.length === 0 && (
          <Typography variant="body2" className={classes.emptyState}>
            {translations?.uploads_panel_no_images || 'No uploaded images available.'}
          </Typography>
        )}

        {isOpen && galleryItems.length > 0 && (
          <Box className={panelMode === 'builder' ? classes.builderGalleryGrid : classes.galleryGrid}>
            {galleryItems.map((item, index) => {
              const imageUrl = item.cdnPublicUrl || item.publicUrl;
              const imageName = item.fileName || item.name || `Image ${index + 1}`;
              const fileSize = item.fileSize ? `${(item.fileSize / 1024).toFixed(2)} KB` : 'Unknown size';
              const contentType = item.contentMimeType || item.type || 'Unknown type';
              const dimensions = item.width && item.height ? `${item.width}x${item.height}` : '';

              if (panelMode === 'builder') {
                return (
                  <Box key={`${imageUrl}-${item.id || index}`} className={classes.imageOnlyGalleryItem}>
                    <Box className={classes.imageOnlyWrapper}>
                      <button
                        type="button"
                        className={classes.imageOnlyButton}
                        onClick={() => handleThumbnailClick(item, imageUrl)}
                        title={onSelectImage ? 'Click to use image' : 'Click to copy image link'}>
                        <img
                          src={imageUrl}
                          alt={imageName}
                          className={classes.imageOnlyImg}
                          loading="lazy"
                        />
                      </button>
                      {typeof onDeleteImage === 'function' && (
                        <IconButton
                          className={classes.deleteOverlayButton}
                          onClick={event => {
                            event.stopPropagation();
                            handleDeleteClick(item);
                          }}
                          title={translations?.uploads_panel_delete_title || 'Delete image'}
                          aria-label={translations?.uploads_panel_delete_title || 'Delete image'}>
                          <DeleteOutlinedIcon className={classes.deleteOverlayIcon} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              }

              return (
                <Box key={`${imageUrl}-${item.id || index}`} className={classes.galleryItem}>
                  <Box className={classes.thumbWrapper}>
                    <img
                      src={imageUrl}
                      alt={imageName}
                      className={classes.thumbImage}
                      loading="lazy"
                      onClick={() => handleThumbnailClick(item, imageUrl)}
                      title={onSelectImage ? 'Click to use image' : 'Click to copy image link'}
                    />
                    <Box className={classes.thumbMeta}>
                      <Box className={classes.thumbHeader}>
                        <Typography className={classes.thumbName} title={imageName}>
                          {imageName}
                        </Typography>
                        <IconButton
                          className={classes.copyIconButton}
                          onClick={event => {
                            event.stopPropagation();
                            copyToClipboard(imageUrl);
                          }}
                          title="Copy URL"
                          aria-label="Copy URL">
                          <FileCopyOutlinedIcon className={classes.copyIcon} />
                        </IconButton>
                        {typeof onDeleteImage === 'function' && (
                          <IconButton
                            className={classes.deleteIconButton}
                            onClick={event => {
                              event.stopPropagation();
                              handleDeleteClick(item);
                            }}
                            title={translations?.uploads_panel_delete_title || 'Delete image'}
                            aria-label={translations?.uploads_panel_delete_title || 'Delete image'}>
                            <DeleteOutlinedIcon className={classes.deleteIcon} />
                          </IconButton>
                        )}
                      </Box>
                      <Typography className={classes.thumbSize}>{fileSize}</Typography>
                      <Typography className={classes.thumbType}>{contentType}</Typography>
                      {dimensions && <Typography className={classes.thumbSize}>{dimensions}</Typography>}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {copyStatus && <Typography className={classes.copyStatus}>{copyStatus}</Typography>}
      </Box>

      <PopupModal
        isOpen={showDeleteConfirm}
        handleClose={handleDeleteCancel}
        title={translations?.uploads_panel_delete_title || 'Delete Image'}
        severity="warning"
        content={translations?.deleteImageConfirmation || 'Are you sure you want to delete this image? This action will remove it from both Template Uploads and Uploads page'}
        buttonGroup={getDeleteButtonGroup()}
      />
    </Box>
  );
};

export default UploadsPanel;
